import { Money } from '@klear/money'
import { DEFAULT_CURRENCY_CODE, type CurrencyCode } from '@klear/constants/currency'
import { operationalLineLabel } from '@klear/operational/presentation'
import type { OperationalRead } from '@klear/operational/operational-read'
import type { PaymentItem } from '@klear/payment/item'
import {
  PAYMENT_LINE_ITEM_SOURCE_ITEM,
  PAYMENT_LINE_ITEM_SOURCE_ORDER_EVENT,
  PAYMENT_LINE_ITEM_TYPE_LABELS,
  type PaymentLineItemType
} from '@klear/payment/constants'
import type { PaymentLineItem } from '@klear/payment/line-item'

function itemLabel(type: PaymentLineItemType, note?: string): string {
  if (note != null && note.trim().length > 0) return note.trim()
  return PAYMENT_LINE_ITEM_TYPE_LABELS[type] ?? type
}

export type PayableEventAllocation = {
  orderEventId: number
  allocated: Money
}

export function payableLineItemsFrom(
  operationals: OperationalRead[],
  items: PaymentItem[],
  allocations: PayableEventAllocation[] = [],
  fallbackCurrency: CurrencyCode = DEFAULT_CURRENCY_CODE
): PaymentLineItem[] {
  const zero = Money.zero(fallbackCurrency)
  const allocationByEvent = new Map(allocations.map((entry) => [entry.orderEventId, entry.allocated]))

  const events: PaymentLineItem[] = operationals.map((operational) => {
    const live = operational.amount ?? zero
    const allocated = (operational.id != null ? allocationByEvent.get(operational.id) : undefined) ?? live
    return {
      source: PAYMENT_LINE_ITEM_SOURCE_ORDER_EVENT,
      label: operationalLineLabel(operational),
      amount: allocated,
      eventAmount: live,
      orderEventId: operational.id ?? undefined,
      orderId: operational.orderId ?? undefined,
      parentEventId: operational.parentEventId ?? undefined,
      effectiveDate: operational.effectiveDate?.toString()
    }
  })

  const carried: PaymentLineItem[] = items.map((item) => ({
    source: PAYMENT_LINE_ITEM_SOURCE_ITEM,
    label: itemLabel(item.type, item.note),
    amount: item.amount,
    itemType: item.type,
    note: item.note ?? undefined
  }))

  const resolvedEventIds = new Set(operationals.map((operational) => operational.id).filter((id) => id != null))
  const orphaned: PaymentLineItem[] = allocations
    .filter((entry) => !resolvedEventIds.has(entry.orderEventId))
    .map((entry) => ({
      source: PAYMENT_LINE_ITEM_SOURCE_ORDER_EVENT,
      label: `Deleted event #${entry.orderEventId}`,
      amount: entry.allocated,
      orderEventId: entry.orderEventId,
      unresolved: true
    }))

  return [...events, ...orphaned, ...carried]
}
